import type { TemplateField } from "@/api/researchCms.service";

export type FieldSection =
  | "representative_matters"
  | "key_personnel"
  | "key_clients"
  | "sar_scores"
  | "team_size"
  | "internal_notes";

export type SubmissionFieldMapping = {
  section: FieldSection;
  /** For fields backed by a string[] or {text}[] in OrgFormValues */
  formArrayName?: string;
  /** For scalar fields (teamSizePartners, editorCommentBox) */
  formPath?: string;
};

const FIELD_NAME_MAP: Record<string, SubmissionFieldMapping> = {
  practice_heads: { section: "key_personnel", formArrayName: "practiceHeads" },
  individual_practice_heads: { section: "key_personnel", formArrayName: "practiceHeads" },
  other_key_lawyers: { section: "key_personnel", formArrayName: "otherKeyLawyers" },
  individual_other_key_lawyers: { section: "key_personnel", formArrayName: "otherKeyLawyers" },
  key_clients: { section: "key_clients", formArrayName: "keyClients" },
  individual_key_clients: { section: "key_clients", formArrayName: "keyClients" },
  representative_matters: { section: "representative_matters", formArrayName: "matterSummaries" },
  individual_representative_matters: {
    section: "representative_matters",
    formArrayName: "matterSummaries",
  },
  team_size_partners: { section: "team_size", formPath: "teamSizePartners" },
  individual_team_size_partners: { section: "team_size", formPath: "teamSizePartners" },
  team_size_lawyers: { section: "team_size", formPath: "teamSizeLawyers" },
  individual_team_size_lawyers: { section: "team_size", formPath: "teamSizeLawyers" },
  editor_comment_box: { section: "internal_notes", formPath: "editorCommentBox" },
  individual_editor_comment_box: { section: "internal_notes", formPath: "editorCommentBox" },
};

// Fields that live on the submission tab but are not editable in the dynamic tab
// (read-only timestamps, testimonials, etc.).
const SKIP_FIELDS = new Set(["researcher_comments", "editor_comments"]);

/**
 * Maps a template field to its submission-tab rendering info.
 * Returns null for fields that should not appear in the dynamic submission tab
 * (non-editable metadata, testimonials, wrong tab, etc.).
 */
export function mapSubmissionField(field: TemplateField): SubmissionFieldMapping | null {
  if (field.fieldTab !== "submission") return null;
  if (SKIP_FIELDS.has(field.fieldName)) return null;
  if (field.fieldName.startsWith("testimonial_")) return null;

  const explicit = FIELD_NAME_MAP[field.fieldName];
  if (explicit) return explicit;

  // SAR fields (org or individual) map to the sar_scores section
  if (field.sarsScoreKey) {
    return { section: "sar_scores" };
  }

  return null;
}

// Section display order for the dynamic submission tab
const SECTION_ORDER: FieldSection[] = [
  "representative_matters",
  "key_personnel",
  "key_clients",
  "team_size",
  "internal_notes",
  "sar_scores",
];

export type SubmissionFieldGroup = {
  section: FieldSection;
  fields: TemplateField[];
  /** True if any field in the group has publishable === 1 */
  publishable: boolean;
};

/**
 * Filters to editable submission-tab fields, groups by section, and returns
 * groups in a stable display order. Each group carries a publishable flag
 * that is true when any field in the group is publishable.
 */
export function groupSubmissionFields(fields: TemplateField[]): SubmissionFieldGroup[] {
  const groups = new Map<FieldSection, { fields: TemplateField[]; publishable: boolean }>();

  for (const field of fields) {
    const mapping = mapSubmissionField(field);
    if (!mapping) continue;

    let group = groups.get(mapping.section);
    if (!group) {
      group = { fields: [], publishable: false };
      groups.set(mapping.section, group);
    }
    group.fields.push(field);
    if (field.publishable === 1) group.publishable = true;
  }

  const result: SubmissionFieldGroup[] = [];
  for (const section of SECTION_ORDER) {
    const group = groups.get(section);
    if (group) {
      result.push({ section, fields: group.fields, publishable: group.publishable });
    }
  }
  return result;
}
